export const CREW_RESPONSIBILITIES = {
  COLLECT_EQUIPMENT_PACK_IN: 'COLLECT_EQUIPMENT_PACK_IN',
  MEET_AT_VENUE: 'MEET_AT_VENUE',
  NOT_CONFIRMED: 'NOT_CONFIRMED',
} as const;

export type CrewResponsibility = typeof CREW_RESPONSIBILITIES[keyof typeof CREW_RESPONSIBILITIES];

const isExplicitTrue = (value: unknown) => value === true || String(value).toUpperCase() === 'TRUE';

export function getCrewResponsibility(record: Record<string, unknown> | null | undefined): CrewResponsibility {
  const member = record || {};
  const instruction = String(member.arrival_instruction || '').toUpperCase();

  if (instruction === CREW_RESPONSIBILITIES.COLLECT_EQUIPMENT_PACK_IN) {
    return CREW_RESPONSIBILITIES.COLLECT_EQUIPMENT_PACK_IN;
  }
  if (instruction === CREW_RESPONSIBILITIES.MEET_AT_VENUE) {
    return CREW_RESPONSIBILITIES.MEET_AT_VENUE;
  }
  if (instruction === CREW_RESPONSIBILITIES.NOT_CONFIRMED) {
    return CREW_RESPONSIBILITIES.NOT_CONFIRMED;
  }

  // Preserve the meaning of legacy per-assignment responsibility flags until
  // an Admin explicitly chooses one of the canonical selector values.
  if (
    isExplicitTrue(member.pack_in_responsible)
    || isExplicitTrue(member.pack_in_setup_responsible)
    || isExplicitTrue(member.equipment_pickup_required)
    || isExplicitTrue(member.equipment_collection_required)
  ) {
    return CREW_RESPONSIBILITIES.COLLECT_EQUIPMENT_PACK_IN;
  }
  if (isExplicitTrue(member.meet_at_venue)) {
    return CREW_RESPONSIBILITIES.MEET_AT_VENUE;
  }

  return CREW_RESPONSIBILITIES.NOT_CONFIRMED;
}

export function updateCrewMember(
  roster: Record<string, any> | null | undefined,
  memberIndex: number,
  patch: Record<string, unknown>,
): Record<string, any> {
  const crew = (roster?.crew || []).map((member: Record<string, unknown>) => ({ ...member }));
  if (!crew[memberIndex]) throw new Error('Crew member not found');
  crew[memberIndex] = { ...crew[memberIndex], ...patch };
  return { ...(roster || {}), crew };
}

export function withIndividualCallTime(
  roster: Record<string, any> | null | undefined,
  memberIndex: number,
  callTime: string,
): Record<string, any> {
  return updateCrewMember(roster, memberIndex, { call_time: callTime });
}

export function withCrewResponsibility(
  roster: Record<string, any> | null | undefined,
  memberIndex: number,
  responsibility: CrewResponsibility,
): Record<string, any> {
  if (!Object.values(CREW_RESPONSIBILITIES).includes(responsibility)) {
    throw new Error('Invalid crew responsibility');
  }
  return updateCrewMember(roster, memberIndex, { arrival_instruction: responsibility });
}

export function withDefaultCrewCallTime(
  roster: Record<string, any> | null | undefined,
  callTime: string,
): Record<string, any> {
  return { ...(roster || {}), default_call_time: callTime };
}

export function applyDefaultCrewCallTime(
  roster: Record<string, any> | null | undefined,
  memberIndex: number,
): Record<string, any> {
  const defaultCallTime = String(roster?.default_call_time || '');
  if (!defaultCallTime) throw new Error('No default crew call time is set');
  return withIndividualCallTime(roster, memberIndex, defaultCallTime);
}
